import { Component, useEffect, type ErrorInfo, type ReactNode } from 'react';
import { RouterProvider } from '@tanstack/react-router';
import { router, entry } from '@/routes';

type RouteInventory = {
  routes: Array<{
    pattern: string;
    path: string;
    title: string;
    page: string;
    standalone: boolean;
    isPublic: boolean;
    entersFrom?: string;
  }>;
  entry: typeof entry;
};

declare global {
  var __onautoRouteInventory: RouteInventory | undefined;
}

class AppErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo) {}

  render() {
    if (this.state.failed)
      return (
        <main className="mx-auto max-w-xl p-8 text-foreground">
          The bus board couldn't open. Please reload the page.
        </main>
      );
    return this.props.children;
  }
}

export function App() {
  useEffect(() => {
    document.getElementById('root')?.setAttribute('data-app-ready', '1');
    globalThis.__onautoRouteInventory = {
      routes: Object.values(router.routesById)
        .filter((route) => route.id !== '__root__')
        .map((route) => {
          const data = route.options.staticData;
          if (
            !data ||
            !('title' in data) ||
            typeof data.title !== 'string' ||
            !('page' in data) ||
            typeof data.page !== 'string' ||
            !('standalone' in data) ||
            typeof data.standalone !== 'boolean' ||
            !('isPublic' in data) ||
            typeof data.isPublic !== 'boolean'
          )
            throw new Error(`Missing route metadata for ${route.fullPath}`);
          return {
            pattern: route.fullPath.replace(/\$([A-Za-z][A-Za-z0-9]*)/g, '[$1]'),
            path: route.fullPath,
            title: data.title,
            page: data.page,
            standalone: data.standalone,
            isPublic: data.isPublic,
          };
        }),
      entry,
    };
  }, []);

  return (
    <AppErrorBoundary>
      <RouterProvider router={router} />
    </AppErrorBoundary>
  );
}
